import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onClose", "variant", "key", "id", "autoHideDuration"];
import { useDeepEqualMemo } from '@superdispatch/hooks';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Snackbar } from "./Snackbar.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function warnContext() {
  // eslint-disable-next-line no-console
  console.log('`useSnackbarStack` is used outside of `SnackbarStackProvider`.');
}
var Context = /*#__PURE__*/createContext({
  clearStack: warnContext,
  addSnackbar: () => {
    warnContext();
    return warnContext;
  }
});
export function useSnackbarStack() {
  return useContext(Context);
}
export function SnackbarStackConsumer(_ref) {
  var {
    children
  } = _ref;
  return /*#__PURE__*/_jsx(Context.Consumer, {
    children: children
  });
}
var TRANSIENT_KEY = '@@transient@@';
export function SnackbarStackProvider(_ref2) {
  var {
    children
  } = _ref2;
  var [stack, setStack] = useState(new Map());
  var transientKeyRef = useRef(0);
  var getNextTransientKey = useCallback(() => TRANSIENT_KEY + String(transientKeyRef.current += 1), []);
  var clearStack = useCallback(() => {
    setStack(x => x.size === 0 ? x : new Map());
  }, []);
  var addSnackbar = useCallback(function (message) {
    var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      {
        onClose: _onClose,
        variant,
        key = variant !== 'error' ?
        // We don't want non error snackbars without key to pop back.
        getNextTransientKey() : typeof message === 'string' ? message : Math.random(),
        id = String(key),
        autoHideDuration = 5000
      } = _ref3,
      props = _objectWithoutProperties(_ref3, _excluded);
    function removeSnackbar() {
      setStack(prev => {
        if (prev.has(key)) {
          var next = new Map(prev);
          next.delete(key);
          return next;
        }
        return prev;
      });
    }
    setStack(prev => {
      var next = new Map(prev);
      for (var prevKey of prev.keys()) {
        // Ensure that we insert value to the end of the map.
        if (Object.is(key, prevKey)) {
          next.delete(key);
        }

        // Ensure that transient snackbar will not pop back.
        if (typeof prevKey == 'string' && prevKey.startsWith(TRANSIENT_KEY)) {
          next.delete(prevKey);
        }
      }
      return next.set(key, _objectSpread(_objectSpread({}, props), {}, {
        id,
        key,
        variant,
        autoHideDuration,
        children: message,
        onClose: reason => {
          removeSnackbar();
          _onClose === null || _onClose === void 0 ? void 0 : _onClose(reason);
        }
      }));
    });
    return removeSnackbar;
  }, [getNextTransientKey]);
  var snackbarProps = useDeepEqualMemo(prev => {
    var next = Array.from(stack.values()).pop();
    return next ? _objectSpread(_objectSpread({}, next), {}, {
      open: true
    }) : _objectSpread(_objectSpread({}, prev), {}, {
      open: false
    });
  }, [stack]);
  var api = useMemo(() => ({
    clearStack,
    addSnackbar
  }), [clearStack, addSnackbar]);
  return /*#__PURE__*/_jsxs(Context.Provider, {
    value: api,
    children: [children, /*#__PURE__*/_jsx(Snackbar, _objectSpread({}, snackbarProps))]
  });
}